import { createAction } from "@reduxjs/toolkit";
import { REFINE_HISTORY_LIMIT } from "./refineHistory";

export const chartHistoryActions = {
  undoChart: createAction("chartHistory/undo"),
  redoChart: createAction("chartHistory/redo"),
  restoreChart: createAction("chartHistory/restore"),
};

const newCard = (chart, id) => ({ id: String(id), chart, deleted: false, past: [], future: [] });
const rebuild = (charts, nextId) => ({
  cards: charts.map((chart, index) => newCard(chart, nextId + index)),
  nextId: nextId + charts.length,
});

// Stable editor-only identities keep history attached to a chart as its index changes.
// refineData still contains only live charts, so publishing and URL encoding are unchanged.
export const withChartHistory = reducer => (state, action) => {
  const next = reducer(state, action);
  const history = state?.chartHistory || { cards: [], nextId: 0 };
  if (!state || action.type === "main/setRefineData" || action.type === "main/startNewChart" ||
      (action.type === "main/loadChartDocument" && next !== state) ||
      (action.type === "main/setMenuSelected" && ![2, 3].includes(action.payload)) ||
      (action.type === "refineHistory/setEnabled" && !action.payload)) {
    return { ...next, chartHistory: rebuild(next.refineData.perChartData, history.nextId) };
  }
  if (Object.values(chartHistoryActions).some(creator => creator.type === action.type)) {
    if (!state.refineHistory.enabled) return next;
    const card = history.cards.find(card => card.id === action.payload);
    if (!card) return next;
    let replacement;
    if (action.type === chartHistoryActions.restoreChart.type) {
      if (!card.deleted) return next;
      replacement = { ...card, deleted: false };
    } else {
      if (card.deleted) return next;
      const undo = action.type === chartHistoryActions.undoChart.type;
      const source = undo ? card.past : card.future;
      if (!source.length) return next;
      replacement = { ...card, chart: source[source.length - 1],
        past: undo ? card.past.slice(0, -1) : [...card.past, card.chart].slice(-REFINE_HISTORY_LIMIT),
        future: undo ? [...card.future, card.chart].slice(-REFINE_HISTORY_LIMIT) : card.future.slice(0, -1),
      };
    }
    const cards = history.cards.map(item => item.id === card.id ? replacement : item);
    return { ...next, chartHistory: { ...history, cards },
      refineData: { ...next.refineData, perChartData: cards.filter(item => !item.deleted).map(item => item.chart) } };
  }
  if (state.refineData === next.refineData) return next;
  const index = action.type === "main/adjustRefineChartStrings" ? action.payload[0]?.chartIndex
    : typeof action.payload === "number" ? action.payload : action.payload?.chartIndex;
  const card = history.cards.filter(item => !item.deleted)[index];
  if (!card) return { ...next, chartHistory: rebuild(next.refineData.perChartData, history.nextId) };
  const slot = history.cards.findIndex(item => item.id === card.id);
  const cards = [...history.cards];
  let nextId = history.nextId;
  if (action.type === "main/deleteRefineDataElement") {
    cards[slot] = { ...card, deleted: true };
  } else if (action.type === "main/duplicateRefineDataElement") {
    cards.splice(slot, 0, newCard(next.refineData.perChartData[index], nextId++));
  } else {
    cards[slot] = { ...card, chart: next.refineData.perChartData[index], future: [],
      past: state.refineHistory.enabled ? [...card.past, card.chart].slice(-REFINE_HISTORY_LIMIT) : [],
    };
  }
  return { ...next, chartHistory: { cards, nextId } };
};
